import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    
    // Initialize product count tracking for infinite scroll
    this.#initializeProductCount();

    // Listen for layout restoration events
    const grid = this.refs?.grid;
    if (grid) {
      grid.addEventListener('layout-restored', this.#handleLayoutRestored);
    }
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    
    // Clean up layout restoration listener
    const grid = this.refs?.grid;
    if (grid) {
      grid.removeEventListener('layout-restored', this.#handleLayoutRestored);
    }
  }

  /**
   * Handle layout restoration from sessionStorage
   * @param {CustomEvent} event - The layout restored event
   */
  #handleLayoutRestored = (event) => {
    const { layout } = event.detail;
    
    // Ensure the layout is properly applied
    if (this.refs.grid) {
      this.refs.grid.setAttribute('product-grid-view', layout);
    }
  };

  /**
   * Initialize product count tracking
   */
  #initializeProductCount() {
    // Get initial counts
    this.#updateProductCount();
    
    // Watch for new products being added (infinite scroll)
    const grid = this.refs?.grid;
    if (grid) {
      const observer = new MutationObserver(() => {
        this.#updateProductCount();
      });
      
      observer.observe(grid, {
        childList: true,
        subtree: true
      });
    }
  }

  /**
   * Update the product count display
   */
  #updateProductCount() {
    const grid = this.refs?.grid;
    const countElements = document.querySelectorAll('.products-count-wrapper span');
    
    if (!grid || !countElements.length) return;
    
    // Count currently visible product items
    const visibleProducts = grid.querySelectorAll('.product-grid__item').length;
    
    // Get total count from data attribute or page context
    let totalProducts = 0;
    
    // Try to get from page context first
    if (window.collectionData?.total_products) {
      totalProducts = window.collectionData.total_products;
    } else {
      // Fallback: try to extract from existing count text or data attributes
      const mainElement = document.querySelector('main');
      if (mainElement?.dataset.totalProducts) {
        totalProducts = parseInt(mainElement.dataset.totalProducts);
      } else {
        // Last fallback: assume visible count is total (for now)
        totalProducts = visibleProducts;
      }
    }
    
    // Generate the new count text
    const countText = this.#generateCountText(visibleProducts, totalProducts);
    
    // Update all count elements
    countElements.forEach(element => {
      element.textContent = countText;
    });
  }

  /**
   * Generate count text in format "1 - 36 of 7619 Results"
   */
  #generateCountText(visible, total) {
    if (total === 0) {
      return 'No products found';
    }
    
    if (total === 1) {
      return '1 Result';
    }
    
    if (visible === 1 && total === 1) {
      return '1 of 1 Results';
    }
    
    if (visible === total) {
      if (visible === 1) {
        return '1 Result';
      }
      return `1 - ${visible} Results`;
    }
    
    if (visible === 1) {
      return `1 of ${total} Results`;
    }
    
    return `1 - ${visible} of ${total} Results`;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout with animation.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Use startViewTransition properly
    try {
      await startViewTransition(() => this.#setLayout(value), ['product-grid']);
    } catch (error) {
      // Fallback to direct layout change if view transition fails
      this.#setLayout(value);
    }

    // Store the layout in sessionStorage after successful change
    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Applies the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    
    // Set the attribute properly
    grid.setAttribute('product-grid-view', value);
    
    // Force a reflow to ensure CSS changes are applied
    grid.offsetHeight; // Trigger reflow
    
    // Dispatch a custom event to notify other components
    grid.dispatchEvent(new CustomEvent('layout-changed', { 
      detail: { layout: value } 
    }));
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
    
    // Clear stored layout when switching devices to default
    requestIdleCallback(() => {
      const viewport = event.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, 'default');
    });
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}